import type { MiningGroup } from '../types/mining';

// Calcul du coût électrique basé sur les paramètres de minage et la configuration globale
export const calculateElectricityCost = (
  powerConsumptionWh: number,
  miningDurationDays: number,
  electricityRateHC: number,
  electricityRateHP: number,
  solarPanels: boolean,
  solarProductionWh: number = 0
): number => {
  // Validation des paramètres
  if (!powerConsumptionWh || !miningDurationDays || (!electricityRateHC && !electricityRateHP)) {
    return 0;
  }

  // Consommation totale en kWh
  const totalConsumptionKWh = (powerConsumptionWh * 24 * miningDurationDays) / 1000;

  // Production solaire totale en kWh si panneaux présents (solarProductionWh est déjà journalier)
  const totalSolarProductionKWh = solarPanels ? (solarProductionWh * miningDurationDays) / 1000 : 0;

  // Consommation nette (consommation - production solaire)
  const netConsumptionKWh = Math.max(0, totalConsumptionKWh - totalSolarProductionKWh);

  // Répartition HC/HP (assumer 50/50 si pas d'informations plus précises)
  const hcConsumptionKWh = netConsumptionKWh * 0.5;
  const hpConsumptionKWh = netConsumptionKWh * 0.5;

  // Coût total
  return hcConsumptionKWh * electricityRateHC + hpConsumptionKWh * electricityRateHP;
};

// Obtenir les kWh totaux d'un groupe (support mode consommation ET mode kWh direct)
export const getTotalKwhFromGroup = (group: MiningGroup): number => {
  const kwhDirect = Number(group.totalKwhDirect);
  if (!isNaN(kwhDirect) && kwhDirect > 0) {
    return kwhDirect;
  }
  const powerWh = Number(group.powerConsumptionWh);
  const days = Number(group.miningDurationDays);
  if (powerWh > 0 && days > 0) {
    return (powerWh * 24 * days) / 1000;
  }
  return 0;
};

// Calcul du coût électrique basé sur les groupes de minage (support kWh direct)
export const calculateTotalElectricityCostForGroups = (
  groups: MiningGroup[],
  electricityRateHC: number,
  electricityRateHP: number,
  solarPanels: boolean,
  solarProductionWh: number = 0
): number => {
  return groups.reduce((total, group) => {
    // Vérifier le mode utilisé
    if (group.totalKwhDirect !== undefined && group.totalKwhDirect > 0) {
      // Mode kWh direct
      const totalKwhDirect = group.totalKwhDirect;

      // Production solaire totale en kWh (on ne peut pas associer les panneaux à une durée spécifique en mode direct)
      const totalSolarProductionKwh = 0; // Mode direct = pas de calcul solaire automatique

      // Consommation nette
      const netConsumptionKWh = Math.max(0, totalKwhDirect - totalSolarProductionKwh);

      // Répartition HC/HP (50/50)
      const hcConsumptionKWh = netConsumptionKWh * 0.5;
      const hpConsumptionKWh = netConsumptionKWh * 0.5;

      return total + hcConsumptionKWh * electricityRateHC + hpConsumptionKWh * electricityRateHP;
    } else {
      // Mode consommation (ancien mode)
      return (
        total +
        calculateElectricityCost(
          group.powerConsumptionWh || 0,
          group.miningDurationDays || 0,
          electricityRateHC,
          electricityRateHP,
          solarPanels,
          solarProductionWh
        )
      );
    }
  }, 0);
};
